import React, { useState, useMemo, useRef, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate } from 'react-router-dom';
import { FiSearch, FiPlus, FiEye, FiMoreVertical, FiEdit2, FiTrash2, FiCopy, FiX, FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import { apiUrl } from '../../config/api';
import { CATEGORY_LIST, CATEGORY_DELETE } from '../../api/contracts';
import './Page.css';
import './Category.css';

const COL_COUNT = 5;
const MIN_WIDTHS_PX = [90, 140, 90, 160, 80];
const INITIAL_WIDTHS_PCT = [14, 22, 10, 40, 14];
const PAGE_SIZE = 50;

function Category() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState('');
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [actionMenuId, setActionMenuId] = useState(null);
  const [menuPosition, setMenuPosition] = useState(null);
  const [columnWidths, setColumnWidths] = useState(INITIAL_WIDTHS_PCT);
  const [resizeState, setResizeState] = useState({ index: null, startX: 0, startWidths: [] });
  const navigate = useNavigate();
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const tableRef = useRef(null);

  const filteredCategories = useMemo(() => {
    if (!search.trim()) return categories;
    const q = search.trim().toLowerCase();
    return categories.filter(
      (c) => c.id.toLowerCase().includes(q) || c.name.toLowerCase().includes(q)
    );
  }, [categories, search]);

  const totalItems = filteredCategories.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / PAGE_SIZE));
  const startIndex = (currentPage - 1) * PAGE_SIZE;
  const paginatedCategories = filteredCategories.slice(startIndex, startIndex + PAGE_SIZE);
  const displayStart = totalItems === 0 ? 0 : startIndex + 1;
  const displayEnd = totalItems === 0 ? 0 : Math.min(startIndex + PAGE_SIZE, totalItems);

  useEffect(() => {
    setCurrentPage((p) => Math.min(p, totalPages));
  }, [totalPages, totalItems]);

  useEffect(() => {
    setCurrentPage(1);
  }, [search]);

  useLayoutEffect(() => {
    if (!actionMenuId || !triggerRef.current) {
      setMenuPosition(null);
      return;
    }
    const rect = triggerRef.current.getBoundingClientRect();
    setMenuPosition({
      top: rect.bottom + 4,
      left: rect.left,
      width: rect.width,
    });
  }, [actionMenuId]);

  useEffect(() => {
    function handleClickOutside(e) {
      const inTrigger = triggerRef.current?.contains(e.target);
      const inMenu = menuRef.current?.contains(e.target);
      if (actionMenuId && !inTrigger && !inMenu) {
        setActionMenuId(null);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [actionMenuId]);

  useEffect(() => {
    let cancelled = false;
    const token = localStorage.getItem('token');
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    fetch(apiUrl(CATEGORY_LIST.path), { method: CATEGORY_LIST.method, headers })
      .then((res) => res.json().catch(() => ({})))
      .then((data) => {
        if (cancelled) return;
        const list = data.data || data;
        setCategories(Array.isArray(list) ? list : []);
        setListError(Array.isArray(list) ? '' : 'Invalid response from server.');
      })
      .catch((err) => {
        if (!cancelled) {
          setListError(err.message || 'Failed to load parent categories.');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  const handleEdit = (category) => {
    setActionMenuId(null);
    navigate(`/dashboard/cms/category/${category.id}/edit`);
  };

  const handleDuplicate = (category) => {
    setActionMenuId(null);
    navigate('/dashboard/cms/category/create', { state: { duplicateId: category.id } });
  };

  const handleRemove = async (category) => {
    setActionMenuId(null);
    const token = localStorage.getItem('token');
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    try {
      const res = await fetch(apiUrl(CATEGORY_DELETE.pathFor(category.id)), {
        method: CATEGORY_DELETE.method,
        headers,
      });
      if (res.ok) {
        setCategories((prev) => prev.filter((c) => c.id !== category.id));
      }
    } catch {
      // on error keep list as is; could show toast
    }
  };

  const handleResizeStart = (handleIndex, e) => {
    e.preventDefault();
    setResizeState({
      index: handleIndex,
      startX: e.clientX,
      startWidths: [...columnWidths],
    });
  };

  useEffect(() => {
    if (resizeState.index === null) return;
    const table = tableRef.current;
    if (!table) return;

    const onMove = (e) => {
      const tableWidth = table.offsetWidth;
      if (tableWidth <= 0) return;
      const i = resizeState.index;
      const minLeftPct = (MIN_WIDTHS_PX[i] / tableWidth) * 100;
      const minRightPct = (MIN_WIDTHS_PX[i + 1] / tableWidth) * 100;
      const totalTwo = resizeState.startWidths[i] + resizeState.startWidths[i + 1];
      const deltaPct = ((e.clientX - resizeState.startX) / tableWidth) * 100;
      let newLeft = resizeState.startWidths[i] + deltaPct;
      newLeft = Math.max(minLeftPct, Math.min(totalTwo - minRightPct, newLeft));
      const newRight = totalTwo - newLeft;
      setColumnWidths((prev) => {
        const next = [...prev];
        next[i] = newLeft;
        next[i + 1] = newRight;
        return next;
      });
    };
    const onUp = () => setResizeState((s) => ({ ...s, index: null }));

    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    return () => {
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
  }, [resizeState.index, resizeState.startX, resizeState.startWidths]);

  return (
    <div className="page category-page">
      <div className="category-header">
        <h3>Parent categories</h3>
        <p className="category-subtitle">Manage parent categories</p>
      </div>

      <div className="category-toolbar">
        <div className="search-wrap">
          <FiSearch className="search-icon" aria-hidden />
          <input
            type="text"
            className="search-input"
            placeholder="Search by category ID or name..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Search categories"
          />
        </div>
        <Link to="/dashboard/cms/category/create" className="btn-create">
          <FiPlus className="btn-icon" aria-hidden />
          Create parent category
        </Link>
      </div>

      {loading && <p className="create-category-loading">Loading parent categories…</p>}
      {listError && <p className="create-category-error" role="alert">{listError}</p>}

      <div className="table-wrap">
        <table ref={tableRef} className="category-table category-table-resizable">
          <colgroup>
            {columnWidths.map((w, i) => (
              <col key={i} style={{ width: `${w}%`, minWidth: MIN_WIDTHS_PX[i] }} />
            ))}
          </colgroup>
          <thead>
            <tr>
              {['Parent category ID', 'Parent category Name', 'Status', 'Thumbnail URL', 'Action'].map((label, i) => (
                <th
                  key={i}
                  className={i === COL_COUNT - 1 ? 'th-action' : ''}
                  style={{ width: `${columnWidths[i]}%` }}
                >
                  <span className="th-inner">{label}</span>
                  {i < COL_COUNT - 1 && (
                    <span
                      className="resize-handle"
                      onMouseDown={(e) => handleResizeStart(i, e)}
                      role="separator"
                      aria-orientation="vertical"
                      aria-label={`Resize column ${i + 1}`}
                    />
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {paginatedCategories.length === 0 ? (
              <tr>
                <td colSpan={5} className="table-empty">
                  No parent categories found.
                </td>
              </tr>
            ) : (
              paginatedCategories.map((row) => (
                <tr key={row.id}>
                  <td className="cell-id">{row.id}</td>
                  <td className="cell-name">{row.name}</td>
                  <td>
                    <span className={`status-pill status-${row.status.toLowerCase()}`}>
                      {row.status}
                    </span>
                  </td>
                  <td>
                    <div className="cell-thumbnail">
                      <span className="thumbnail-url" title={row.thumbnailUrl || ''}>
                        {(row.thumbnailUrl || '').length > 40
                          ? (row.thumbnailUrl || '').slice(0, 40) + '…'
                          : row.thumbnailUrl || '—'}
                      </span>
                      <button
                        type="button"
                        className="btn-watch"
                        onClick={() => setPreviewUrl(row.thumbnailUrl)}
                        title="View thumbnail"
                        aria-label={`View thumbnail for ${row.name}`}
                      >
                        <FiEye className="watch-icon" />
                      </button>
                    </div>
                  </td>
                  <td className="cell-action">
                    <div className="action-wrap">
                      <button
                        ref={actionMenuId === row.id ? triggerRef : null}
                        type="button"
                        className="btn-dots"
                        onClick={() => setActionMenuId(actionMenuId === row.id ? null : row.id)}
                        aria-label="Open menu"
                        aria-expanded={actionMenuId === row.id}
                      >
                        <FiMoreVertical className="dots-icon" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {totalItems > 0 && (
        <div className="pagination-wrap">
          <button
            type="button"
            className="pagination-btn"
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            aria-label="Previous page"
          >
            <FiChevronLeft className="pagination-arrow" aria-hidden />
          </button>
          <span className="pagination-text">
            Showing {displayStart}–{displayEnd} of {totalItems}
          </span>
          <button
            type="button"
            className="pagination-btn"
            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            disabled={currentPage >= totalPages}
            aria-label="Next page"
          >
            <FiChevronRight className="pagination-arrow" aria-hidden />
          </button>
        </div>
      )}

      {actionMenuId && menuPosition && (() => {
        const category = filteredCategories.find((c) => c.id === actionMenuId);
        if (!category) return null;
        return createPortal(
          <div
            ref={menuRef}
            className="action-menu action-menu-portal"
            style={{ position: 'fixed', top: menuPosition.top, left: menuPosition.left, zIndex: 1000 }}
          >
            <button type="button" className="action-menu-item" onClick={() => handleEdit(category)}>
              <FiEdit2 className="action-menu-icon" />
              Edit category
            </button>
            <button type="button" className="action-menu-item" onClick={() => handleDuplicate(category)}>
              <FiCopy className="action-menu-icon" />
              Duplicate category
            </button>
            <button
              type="button"
              className="action-menu-item"
              disabled={category.status !== 'Inactive'}
              onClick={() => category.status === 'Inactive' && handleRemove(category)}
              title={category.status !== 'Inactive' ? 'Only inactive categories can be removed' : 'Remove category'}
            >
              <FiTrash2 className="action-menu-icon" />
              Remove category
            </button>
          </div>,
          document.body
        );
      })()}

      {previewUrl && (
        <div className="thumbnail-overlay" onClick={() => setPreviewUrl(null)} role="dialog" aria-modal="true" aria-label="Thumbnail preview">
          <div className="thumbnail-modal" onClick={(e) => e.stopPropagation()}>
            <button type="button" className="btn-close-preview" onClick={() => setPreviewUrl(null)} aria-label="Close preview">
              <FiX className="close-icon" />
            </button>
            <img src={previewUrl} alt="Parent category thumbnail" className="thumbnail-preview-img" />
          </div>
        </div>
      )}
    </div>
  );
}

export default Category;
